export type AnnounceMode = 'polite' | 'assertive';

interface Announcement {
  message: string;
  mode: AnnounceMode;
  /** Incrementing token so identical consecutive messages still re-announce. */
  token: number;
}

type Listener = (announcement: Announcement) => void;

/**
 * Framework-agnostic singleton backing the {@link LiveAnnouncer}.
 *
 * The store exists at module scope so `announce()` can be called from anywhere
 * — including outside the React tree — while a mounted `LiveAnnouncer` mirrors
 * the latest announcement into the DOM live region(s).
 */
const listeners = new Set<Listener>();
let token = 0;
let last: Announcement | null = null;

export const liveAnnouncerStore = {
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  getLast(): Announcement | null {
    return last;
  },

  publish(message: string, mode: AnnounceMode = 'polite'): void {
    token += 1;
    last = { message, mode, token };
    for (const listener of listeners) {
      listener(last);
    }
  },
};

/**
 * Imperatively announce a message to assistive technology.
 *
 * Requires a `<LiveAnnouncer />` to be mounted (once, at the app root). Safe to
 * call from anywhere — event handlers, effects, or non-React code.
 *
 * @param message - The text to announce.
 * @param mode - `'polite'` (default) waits for the user to be idle;
 *   `'assertive'` interrupts immediately. Use `'assertive'` sparingly.
 */
export const announce = (
  message: string,
  mode: AnnounceMode = 'polite',
): void => {
  liveAnnouncerStore.publish(message, mode);
};
